<script setup>
import AdminLayout from "../Layouts/AdminLayout.vue";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Tag from "primevue/tag";
import Dropdown from "primevue/dropdown";
import { Inertia } from "@inertiajs/inertia";
import { ref } from "vue";

const props = defineProps({
    notifications: {
        type: Object,
        required: true
    },
    isSuperAdmin: {
        type: Boolean,
        default: false
    },
    companyOptions: {
        type: Array,
        default: () => []
    },
    filters: {
        type: Object,
        default: () => ({
            company_id: null
        })
    }
});

const selectedCompany = ref(props.filters.company_id);

const activeIndex = ref(2); // Set History Log tab as active

const getTypeSeverity = (type) => {
    switch(type) {
        case 'all': return 'info';
        case 'all_male': return 'info';
        case 'all_female': return 'info';
        case 'all_active': return 'info';
        case 'all_active_male': return 'info';
        case 'all_active_female': return 'info';
        case 'many': return 'warning';
        case 'customer': return 'success';
        default: return 'secondary';
    }
};

const getTypeLabel = (type) => {
    return type; // Return the type as-is: all, many, or customer
};

const getStatusSeverity = (status) => {
    switch(status) {
        case 'processing': return 'warning';
        case 'sent': return 'success';
        case 'failed': return 'danger';
        default: return 'secondary';
    }
};

const getStatusLabel = (status) => {
    switch(status) {
        case 'processing': return 'Processing';
        case 'sent': return 'Sent';
        case 'failed': return 'Failed';
        default: return status;
    }
};

const handleTabChange = (event) => {
    // Index 0: Customers
    // Index 1: Birthdays
    // Index 2: History Log
    
    if (event.index === 0) {
        Inertia.visit('/admin/notifications');
    } else if (event.index === 1) {
        Inertia.visit('/admin/notifications/birthdays');
    }
};

const handleFilterChange = () => {
    const params = {};

    if (props.isSuperAdmin) {
        params.company_id = selectedCompany.value;
    }

    Inertia.get('/admin/notifications/history', params, {
        preserveState: true,
        preserveScroll: true
    });
};

const viewDetails = (notification) => {
    Inertia.visit(`/admin/notifications/history/${notification.uuid}`);
};

const channelLabel = (channel) => {
    return channel === 'email' ? 'Email' : 'Push';
};
</script>

<template>
    <AdminLayout>
        <div class="p-6">
            <div class="flex justify-between items-center mb-6">
                <h1 class="text-2xl font-semibold">Notifications Dashboard</h1>
            </div>

            <TabView :activeIndex="activeIndex" @tab-change="handleTabChange">
                <TabPanel header="Customers">
                    <!-- This tab will redirect to main page when clicked -->
                </TabPanel>

                <TabPanel header="Birthdays">
                    <!-- This tab will redirect to birthdays page when clicked -->
                </TabPanel>

                <TabPanel header="History Log">
                    <div v-if="isSuperAdmin" class="mb-4 flex justify-end">
                        <div class="w-64">
                            <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Unternehmen</label>
                            <Dropdown
                                v-model="selectedCompany"
                                :options="companyOptions"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Alle Unternehmen"
                                @change="handleFilterChange"
                                class="w-full !h-[42px] flex items-center"
                                showClear
                                filter
                            />
                        </div>
                    </div>

                    <DataTable 
                        :value="notifications.data" 
                        :paginator="true"
                        :rows="20"
                        :totalRecords="notifications.total"
                        :lazy="true"
                        responsiveLayout="scroll"
                        class="mt-4"
                    >
                        <Column field="title" header="Title" :sortable="true" style="min-width: 200px">
                            <template #body="{ data }">
                                <div class="font-medium">{{ data.title }}</div>
                                <div class="text-sm text-gray-500 mt-1">{{ data.body }}</div>
                            </template>
                        </Column>
                        
                        <Column field="type" header="Type" :sortable="true" style="width: 120px">
                            <template #body="{ data }">
                                <Tag :value="data.type" :severity="getTypeSeverity(data.type)" />
                            </template>
                        </Column>

                        <Column header="Channels" style="min-width: 180px">
                            <template #body="{ data }">
                                <div class="flex flex-wrap gap-1">
                                    <Tag
                                        v-for="channel in data.channels"
                                        :key="channel"
                                        :value="channelLabel(channel)"
                                        severity="info"
                                    />
                                </div>
                            </template>
                        </Column>
                        
                        <Column field="status" header="Status" :sortable="true" style="width: 120px">
                            <template #body="{ data }">
                                <Tag :value="getStatusLabel(data.status)" :severity="getStatusSeverity(data.status)" />
                            </template>
                        </Column>
                        
                        <Column field="created_at" header="Sent At" :sortable="true" style="width: 180px">
                            <template #body="{ data }">
                                <div>{{ data.created_at }}</div>
                                <div class="text-sm text-gray-500">{{ data.created_at_human }}</div>
                            </template>
                        </Column>
                        
                        <Column header="Actions" style="width: 100px">
                            <template #body="{ data }">
                                <Button 
                                    icon="pi pi-eye" 
                                    severity="secondary" 
                                    text 
                                    size="small"
                                    @click="viewDetails(data)"
                                    title="View Details"
                                />
                            </template>
                        </Column>
                    </DataTable>
                </TabPanel>
            </TabView>
        </div>
    </AdminLayout>
</template>